import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';

export function ProtectedRoute(props: { children: React.ReactNode }) {
  // Safely access children, providing a default empty fragment if props is undefined
  const { children = <></> } = props || {};
  const auth = useAuth();
  const { user, isLoading } = auth || {};
  const isTokenSynced = auth?.isTokenSynced || false;
  const [isVerifying, setIsVerifying] = useState(true);
  const location = useLocation();

  // Add a short verification delay to ensure token sync is complete
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setIsVerifying(false);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, isTokenSynced]);

  // Add debug logging for troubleshooting
  useEffect(() => {
    console.log('[ProtectedRoute] Auth state:', { 
      userExists: !!user, 
      isLoading, 
      isTokenSynced,
      isVerifying,
      token: localStorage.getItem('auth_token') ? 'Present' : 'Missing'
    });
  }, [user, isLoading, isTokenSynced, isVerifying]);

  if (isLoading || isVerifying) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-sm text-gray-600">
          {isVerifying ? "Verifying your session..." : "Loading..."}
        </p>
      </div>
    );
  }

  // If the token is missing but we've passed the loading state, redirect to login
  const token = localStorage.getItem('auth_token');
  if (!token || !user || !isTokenSynced) {
    console.log('[ProtectedRoute] Redirecting to login:', { 
      tokenMissing: !token,
      userMissing: !user,
      tokenNotSynced: !isTokenSynced
    });
    
    // Save the current location to redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}